import { Component, effect, inject, input, computed, output } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { CommonModule } from '@angular/common';
import { settingStore } from "@core/store";
import {MenuItem} from 'primeng/api';
import { MenuBuilderService } from "@shared/services/menu-builder.service";
import { TranslateModule } from '@ngx-translate/core';
import {CondominiumSwitchComponent} from '../condominium-switch/condominium-switch.component';
import { TabStateService } from '@shared/services/tab-state.service';



@Component({
  selector: 'app-sidebar',
  standalone: true,
  templateUrl: './sidebar.component.html',
  styleUrl: './sidebar.component.css',
  imports: [
    RouterLink,
    RouterLinkActive,
    CommonModule,
    TranslateModule,
    CondominiumSwitchComponent
  ]
})
export class SidebarComponent {
  isCollapsed = input<boolean>(false);
  isMobileOpen = input<boolean>(false);

  // Output event para comunicar cambios al componente padre
  collapsedChange = output<boolean>();

  settingStore = inject(settingStore);
  private menuBuilder = inject(MenuBuilderService);
  private tabService = inject(TabStateService);

  // Computed signal que reactivamente construye el menú basado en el rol actual
  menuItems = computed<MenuItem[]>(() => this.menuBuilder.buildMenu());

  // Obtiene el tab activo actual
  private getActiveTab() {
    const tabs = this.tabService.getTabs();
    const activeIndex = this.tabService.getActiveIndex();
    return tabs[activeIndex] || null;
  }

  // Computed signal que determina la ruta activa basándose en el tab activo
  activeRoute = computed(() => {
    const activeTab = this.getActiveTab();
    return activeTab?.route || null;
  });

  constructor() {
    // El layout principal maneja el estado del sidebar, no necesitamos el store aquí
    
    // Effect para expandir submenús padre cuando un hijo está activo
    // Se ejecuta cuando cambia el activeRoute (que depende del tab activo)
    effect(() => {
      const route = this.activeRoute();
      if (route) {
        this.expandParentSubmenusForRoute(route);
      }
    });
  }


  toggleSubmenu(item: MenuItem, event: Event) {
    // event.preventDefault();
    // event.stopPropagation();

    // When collapsed, submenus are handled by CSS hover, not click
    if (this.isCollapsed()) {
      return;
    }

    if (item['hasSubmenu']) {
      item.expanded = !item.expanded;

      // // Cerrar otros submenús si se abre uno nuevo
      // this.menuItems().forEach(menuItem => {
      //   if (menuItem !== item && menuItem.hasSubmenu) {
      //     menuItem.expanded = false;
      //   }
      // });
    }
  }

  // Verifica si un menu item está activo (recursivamente)
  isMenuItemActive(item: MenuItem): boolean {
    const activeRoute = this.activeRoute();
    if (!activeRoute || !item.routerLink) {
      return false;
    }

    // Normalizar rutas para comparación (remover barras iniciales/finales y comparar)
    const normalizeRoute = (route: string | string[] | undefined): string => {
      if (!route) return '';
      const routeStr = Array.isArray(route) ? route.join('/') : route;
      return routeStr.replace(/^\/+|\/+$/g, '').toLowerCase();
    };

    const itemRoute = normalizeRoute(item.routerLink);
    const currentRoute = normalizeRoute(activeRoute);

    // Comparación exacta o si la ruta activa empieza con la ruta del item
    if (itemRoute === currentRoute || currentRoute.startsWith(itemRoute + '/')) {
      return true;
    }

    // Verificar recursivamente en subitems
    if (item.items && item.items.length > 0) {
      return item.items.some(subItem => this.isMenuItemActive(subItem));
    }

    return false;
  }

  // Verifica si un item hijo dentro de un submenu está activo
  hasActiveChild(item: MenuItem): boolean {
    if (!item.items || item.items.length === 0) {
      return false;
    }
    return item.items.some(subItem => this.isMenuItemActive(subItem));
  }

  // Expande automáticamente los submenús padre para una ruta dada
  private expandParentSubmenusForRoute(route: string): void {
    const menuItems = this.menuItems();
    
    const findAndExpandParents = (items: MenuItem[], targetRoute: string, parents: MenuItem[] = []): void => {
      for (const item of items) {
        // Verificar si este item coincide con la ruta
        if (item.routerLink && this.routesMatch(item.routerLink, targetRoute)) {
          // Expandir todos los padres
          parents.forEach(parent => {
            if (parent['hasSubmenu']) {
              parent.expanded = true;
            }
          });
          return;
        }

        // Si tiene subitems, buscar recursivamente
        if (item.items && item.items.length > 0) {
          findAndExpandParents(item.items, targetRoute, [...parents, item]);
        }
      }
    };

    findAndExpandParents(menuItems, route);
  }

  // Normaliza y compara rutas
  private routesMatch(route1: string | string[] | undefined, route2: string | undefined): boolean {
    if (!route1 || !route2) return false;
    
    const normalizeRoute = (route: string | string[]): string => {
      const routeStr = Array.isArray(route) ? route.join('/') : route;
      return routeStr.replace(/^\/+|\/+$/g, '').toLowerCase();
    };

    const normalized1 = normalizeRoute(route1);
    const normalized2 = normalizeRoute(route2);

    return normalized1 === normalized2 || normalized2.startsWith(normalized1 + '/');
  }

  onCollapsedChange(collapsed: boolean): void {
    this.collapsedChange.emit(collapsed);
  }
}
